import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, getApiError } from '@/services/api';
import { useBrandingStore } from '@/store/branding.store';
import { LOGO_REVERSE } from '@/hooks/useThemedLogo';
import { CheckCircle, Loader2, XCircle, Eye, EyeOff } from 'lucide-react';
import { UserRole } from '@/types';
import { Optional } from '@/components/common/Optional';

interface InviteInfo {
  email:     string;
  role:      UserRole;
  expiresAt: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN:                'Administrator',
  RISK_MANAGER:         'Risk Manager',
  COMPLIANCE_OFFICER:   'Compliance Officer',
  AUDITOR:              'Auditor',
  VIEWER:               'Viewer',
  CLIENT_ADMIN:         'Client Administrator',
  CLIENT_USER:          'Client User',
};

export default function AcceptInvitePage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token') ?? '';
  const store      = useBrandingStore();

  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [department, setDepartment] = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [showPass,   setShowPass]   = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [success,    setSuccess]    = useState(false);

  // Fetch invite info to show email / role
  const { data: invite, isLoading, error } = useQuery<InviteInfo>({
    queryKey: ['invite', token],
    queryFn:  () => api.get<{ data: InviteInfo }>(`/users/invite/${token}`).then(r => r.data.data),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: (body: { firstName: string; lastName: string; department?: string; password: string }) =>
      api.post(`/users/invite/${token}/accept`, body),
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    },
    onError: (err) => setFormErr(getApiError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErr('');
    if (!firstName.trim() || !lastName.trim()) { setFormErr('First and last name are required.'); return; }
    if (password.length < 8)                   { setFormErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm)                  { setFormErr('Passwords do not match.'); return; }
    acceptMutation.mutate({ firstName, lastName, department: department || undefined, password });
  };

  const inputClass = 'block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="min-h-screen bg-surface-2 flex flex-col items-center justify-center py-12 px-4">
      {/* Header card: navy with the orange top edge and the reverse logo
          (design system › SignInCard). */}
      <div className="w-full max-w-md rounded-t-2xl border-t-[3px] border-dl-orange bg-navy px-8 py-6 flex flex-col items-center">
        <img src={LOGO_REVERSE} alt="Dual Layer IT" className="h-10 w-auto object-contain mb-3" />
        <h1 className="text-xl text-ink-d">{store.appName}</h1>
        <p className="text-sm text-ink-soft mt-1">Accept your invitation</p>
      </div>

      {/* Body card */}
      <div className="w-full max-w-md bg-surface rounded-b-2xl shadow-lg px-8 py-8">
        {!token && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <XCircle className="h-5 w-5" /> No invitation token provided.
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <XCircle className="h-5 w-5" /> {getApiError(error)} — this invitation may have expired or already been used.
          </div>
        )}

        {success && (
          <div className="flex flex-col items-center gap-3 text-center py-6">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="text-lg font-semibold text-gray-900">Account created!</p>
            <p className="text-sm text-gray-500">You will be redirected to the login page in a moment…</p>
          </div>
        )}

        {invite && !success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invite info */}
            <div className="rounded-lg bg-brand-50 border border-brand-100 px-4 py-3 text-sm space-y-1">
              <p className="text-gray-700"><span className="font-medium">Invited email:</span> {invite.email}</p>
              <p className="text-gray-700"><span className="font-medium">Role:</span> {ROLE_LABELS[invite.role] ?? invite.role}</p>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>First Name</label>
                <input type="text" className={inputClass} value={firstName} onChange={e => setFirstName(e.target.value)} required autoFocus />
              </div>
              <div>
                <label className={labelClass}>Last Name</label>
                <input type="text" className={inputClass} value={lastName} onChange={e => setLastName(e.target.value)} required />
              </div>
            </div>

            {/* Department */}
            <div>
              <label className={labelClass}>Department <Optional /></label>
              <input type="text" className={inputClass} value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. IT, Finance" />
            </div>

            {/* Password */}
            <div>
              <label className={labelClass}>Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className={`${inputClass} pr-10`}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                />
                <button type="button" onClick={() => setShowPass(v => !v)} aria-label={showPass ? 'Hide password' : 'Show password'} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className={labelClass}>Confirm Password</label>
              <input
                type="password"
                className={inputClass}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                placeholder="Repeat password"
              />
            </div>

            {/* Error */}
            {(formErr || acceptMutation.isError) && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                <XCircle className="h-4 w-4 shrink-0" />
                {formErr || getApiError(acceptMutation.error)}
              </div>
            )}

            <button
              type="submit"
              disabled={acceptMutation.isPending}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {acceptMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Account
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
